import * as IF from "infrontjs";
import template from "./template.html";

import { LogoutState } from "../../../states/auth/LogoutState";
import { ProfileState } from "../../../states/account/ProfileState";
import { SettingsState } from "../../../states/account/SettingsState";
import { LANGUAGES } from "../../../i18n";

/**
 * <user-menu> — the topbar account dropdown (avatar + language + profile /
 * settings / logout). Reads the logged-in user from app.user and owns its own
 * open/close behaviour (trigger toggle, outside-click + Escape to close,
 * logout). Rendered into the light DOM, so it inherits the shared .usermenu
 * styles from admin.css.
 *
 * The language switcher lives here because it is a per-operator preference, not
 * a screen: the account menu is where the rest of them will end up too.
 */
class UserMenu extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
        // Bound once so add/removeEventListener reference the same function.
        this._onDocClick = this._onDocClick.bind( this );
        this._onKeydown  = this._onKeydown.bind( this );
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this._connected = true;
        }

        document.addEventListener( "click", this._onDocClick );
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        // app-layout re-renders by replacing innerHTML — clean up so the
        // document listeners don't leak onto a detached node.
        document.removeEventListener( "click", this._onDocClick );
        document.removeEventListener( "keydown", this._onKeydown );
    }

    render()
    {
        this.app.view.render( this, template, {
            user            : this.app.user,
            languages       : LANGUAGES,
            currentLanguage : this.app.l18n.getCurrentLanguage()
        } );
        this.bind();
    }

    bind()
    {
        this.menu = this.querySelector( '[data-id="usermenu"]' );
        const trigger = this.querySelector( '[data-id="usermenu-trigger"]' );
        const logout  = this.querySelector( '[data-id="logout"]' );

        // Switching reloads the page (see App.switchLanguage), so there is
        // nothing to re-render here — picking the active language is a no-op.
        this.querySelectorAll( '[data-id="lang"]' ).forEach( ( btn ) =>
        {
            btn.addEventListener( "click", ( e ) =>
            {
                e.preventDefault();
                this.app.switchLanguage( btn.getAttribute( "data-lang" ) );
            } );
        } );

        trigger?.addEventListener( "click", ( e ) =>
        {
            e.stopPropagation();
            const open = this.menu.classList.toggle( "is-open" );
            trigger.setAttribute( "aria-expanded", String( open ) );
        } );

        // Account pages: "Mein Profil" and "Einstellungen".
        [ [ "profile", ProfileState.ROUTE ], [ "settings", SettingsState.ROUTE ] ].forEach( ( [ id, route ] ) =>
        {
            this.querySelector( '[data-id="' + id + '"]' )?.addEventListener( "click", ( e ) =>
            {
                e.preventDefault();
                this.close();
                this.app.router.redirect( route );
            } );
        } );

        logout?.addEventListener( "click", ( e ) =>
        {
            e.preventDefault();
            this.close();
            // LogoutState tears down the session (local token + backend DELETE).
            this.app.router.redirect( LogoutState.ROUTE );
        } );
    }

    close()
    {
        this.menu?.classList.remove( "is-open" );
        this.querySelector( '[data-id="usermenu-trigger"]' )?.setAttribute( "aria-expanded", "false" );
    }

    _onDocClick( e )
    {
        if ( this.menu && !this.menu.contains( e.target ) )
        {
            this.close();
        }
    }

    _onKeydown( e )
    {
        if ( e.key === "Escape" )
        {
            this.close();
        }
    }
}

customElements.define( "user-menu", UserMenu );
